import {
  CollectionReference,
  QueryDocumentSnapshot,
  collection,
  onSnapshot,
  doc,
  query,
  where,
  DocumentReference,
  addDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore'
import { orderBy } from 'lodash'
import { useEffect, useState } from 'react'
import { Lab, UserLab } from '../types/bloodWork'
import {
  getEffectiveServices,
  primaryServices,
} from '../utils/firebaseServiceContext'

const userLabsPath = 'user-labs'

const labsCollection = collection(
  primaryServices.firestore,
  'labs',
) as CollectionReference<Lab>

const effectiveLabsCollection = () =>
  collection(
    getEffectiveServices().firestore,
    'labs',
  ) as CollectionReference<Lab>

const effectiveUserLabsCollection = () =>
  collection(
    getEffectiveServices().firestore,
    userLabsPath,
  ) as CollectionReference<UserLab>

export const useLabs = ({
  title,
  isArchived,
}: {
  title?: string
  isArchived?: boolean
}): [QueryDocumentSnapshot<Lab>[] | undefined, boolean | undefined] => {
  const [labs, setLabs] = useState<QueryDocumentSnapshot<Lab>[]>([])
  const [found, setFound] = useState<boolean | undefined>()

  useEffect(() => {
    const conditions = []

    if (isArchived !== undefined) {
      conditions.push(where('archived', '==', isArchived))
    }
    if (title !== undefined) {
      conditions.push(where('title', '==', title))
    }

    const unsub = onSnapshot(
      query(effectiveLabsCollection(), ...conditions),
      (snap) => {
        if (snap.empty) {
          setFound(false)
        } else {
          setLabs(orderBy(snap.docs, (doc) => doc.data().title, 'asc'))
          setFound(true)
        }
      },
      (error) => {
        console.error('Error retrieving document:', error)
        setFound(false)
      }
    )

    return () => unsub()
  }, [title, isArchived])

  return [labs, found]
}

export const useUserLab = ({
  userId,
  userLabId,
  isArchived,
}: {
  userLabId: string
  userId?: string
  isArchived?: boolean
}): [QueryDocumentSnapshot<UserLab> | undefined, boolean | undefined] => {
  const [userLab, setUserLab] = useState<QueryDocumentSnapshot<UserLab>>()
  const [found, setFound] = useState<boolean | undefined>()

  useEffect(() => {
    const docRef = doc(
      getEffectiveServices().firestore,
      userLabsPath,
      userLabId
    ) as DocumentReference<UserLab>

    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data()
          // Only check userId since it can't be part of the query
          if (userId && data.userId !== userId) {
            console.log('No document found for this user')
            setFound(false)
            setUserLab(undefined)
          } else if (isArchived !== undefined && data.archived !== isArchived) {
            console.log(
              `Document has wrong archive status: ${data.archived} !== ${isArchived}`
            )
            setFound(false)
            setUserLab(undefined)
          } else {
            setUserLab(docSnapshot)
            setFound(true)
          }
        } else {
          console.log('No document found with this ID', userLabId)
          setFound(false)
          setUserLab(undefined)
        }
      },
      (error) => {
        console.error('Error retrieving document:', error)
        setFound(false)
        setUserLab(undefined)
      }
    )

    return () => unsubscribe()
  }, [userLabId, isArchived, userId])

  return [userLab, found]
}

export const useUserLabs = ({
  userId,
  title,
  isArchived,
}: {
  userId?: string
  title?: string
  isArchived?: boolean
}): [QueryDocumentSnapshot<UserLab>[] | undefined, boolean | undefined] => {
  const [userLabs, setUserLabs] = useState<QueryDocumentSnapshot<UserLab>[]>([])
  const [found, setFound] = useState<boolean | undefined>()

  useEffect(() => {
    const conditions = []

    if (userId !== undefined) {
      conditions.push(where('userId', '==', userId))
    }
    if (title !== undefined) {
      conditions.push(where('title', '==', title))
    }
    if (isArchived !== undefined) {
      conditions.push(where('archived', '==', isArchived))
    }

    const unsub = onSnapshot(
      query(effectiveUserLabsCollection(), ...conditions),
      (snap) => {
        if (snap.empty) {
          setFound(false)
        } else {
          setUserLabs(orderBy(snap.docs, (doc) => doc.data().date, 'desc'))
          setFound(true)
        }
      },
      (error) => {
        console.error('Error retrieving document:', error)
        setFound(false)
      }
    )

    return () => unsub()
  }, [title, isArchived, userId])

  return [userLabs, found]
}

export const userLabsStore = {
  add(userLab: UserLab) {
    return addDoc(effectiveUserLabsCollection(), userLab)
  },
  update(userLabId: string, userLab: Partial<UserLab>) {
    const docRef = doc(
      getEffectiveServices().firestore,
      userLabsPath,
      userLabId
    ) as DocumentReference<UserLab>

    return updateDoc(docRef, userLab)
  },
  archive(userLabId: string) {
    return this.update(userLabId, { archived: true })
  },
  unarchive(userLabId: string) {
    return this.update(userLabId, { archived: false })
  },
}

export const labToUserLab = (lab: Lab, userId: string): UserLab => {
  const activePanels = lab.panels.filter((panel) => {
    return !panel.archived
  })

  const userLab: UserLab = {
    userId: userId,
    title: lab.title,
    archived: false,
    date: Timestamp.fromDate(new Date()),
    panels: activePanels.map((panel) => {
      const activeItems = panel.items.filter((item) => {
        return !item.archived
      })
      return {
        title: panel.title,
        ...(panel.requiresProvocationType && {
          requiresProvocationType: true,
        }),
        ...(panel.requiresProvocationType && { provocationType: '' }),
        items: activeItems.map((item) => {
          let initialUnit = ''
          const units = item.units.map((unit) => {
            if (!initialUnit) initialUnit = unit.id
            return {
              id: unit.id,
              referenceRange: { ...unit.referenceRange },
              optimalRange: {
                top: unit.optimalRange?.top ?? '',
                bottom: unit.optimalRange?.bottom ?? '',
              },
            }
          })
          return {
            title: item.title,
            unit: initialUnit,
            referenceRange: { ...item.referenceRange },
            optimalRange: {
              top: item.optimalRange?.top ?? '',
              bottom: item.optimalRange?.bottom ?? '',
            },
            result: '',
            units: units,
          }
        }),
      }
    }),
  }

  return userLab
}

export const addNewPanelsToUserLab = (lab: Lab, userLab: UserLab): UserLab => {
  lab.panels.forEach((labPanel, panelIdx) => {
    if (labPanel.archived) {
      return
    }
    const foundPanel = userLab.panels.find(
      (userPanel) => userPanel.title === labPanel.title
    )

    if (!foundPanel) {
      userLab.panels.push({
        title: labPanel.title,
        ...(labPanel.requiresProvocationType && {
          requiresProvocationType: true,
        }),
        ...(labPanel.requiresProvocationType && { provocationType: '' }),
        items: labPanel.items.map((item) => {
          return {
            ...item,
            unit: '',
            result: '',
            referenceRange: { ...item.referenceRange },
            optimalRange: {
              top: item.optimalRange?.top ?? '',
              bottom: item.optimalRange?.bottom ?? '',
            },
            units: item.units?.map((unit) => {
              return {
                id: unit.id,
                referenceRange: { ...unit.referenceRange },
                optimalRange: {
                  top: unit.optimalRange?.top ?? '',
                  bottom: unit.optimalRange?.bottom ?? '',
                },
              }
            }),
          }
        }),
      })
    } else {
      //Panel found. Now check for new items
      labPanel.items.forEach((item, itemIdx) => {
        if (item.archived) {
          return
        }
        let foundItem = foundPanel.items.find(
          (userItem) => item.title === userItem.title
        )
        if (!foundItem) {
          foundPanel.items.push({
            ...item,
            unit: '',
            result: '',
            referenceRange: { ...item.referenceRange },
            optimalRange: {
              top: item.optimalRange?.top ?? '',
              bottom: item.optimalRange?.bottom ?? '',
            },
            units: item.units?.map((unit) => {
              return {
                id: unit.id,
                referenceRange: { ...unit.referenceRange },
                optimalRange: {
                  top: unit.optimalRange?.top ?? '',
                  bottom: unit.optimalRange?.bottom ?? '',
                },
              }
            }),
          })
        } else {
          //Item found. Now check for new units
          item.units?.forEach((unit) => {
            let foundUnit
            //typescript bug. This check shouldn't be necessary
            if (foundItem) {
              if (!foundItem.units) {
                foundItem.units = []
              }

              foundUnit = foundItem.units.find(
                (userUnit) => unit.id === userUnit.id
              )

              if (!foundUnit) {
                foundItem.units.push({
                  id: unit.id,
                  referenceRange: { ...unit.referenceRange },
                  optimalRange: {
                    top: unit.optimalRange?.top ?? '',
                    bottom: unit.optimalRange?.bottom ?? '',
                  },
                })
              }
            }
          })
        }
      })
    }
  })

  return userLab
}

export default labsCollection
